import { AxiosResponse } from 'axios';
import { environment } from '../../../../environments/environment';
import { EndpointHelper } from '../endpoint.helper';
import { HttpClient } from '../http';
import { ChannelContent, ChannelGenreContent, ContentService } from './content';
import { StorageHelper } from '../storage';
import { CacheAreaKey } from '../../config/cache-config';

export type PrincipalPackage = {
  serviceName: string;
  description: string;
  price: number;
  extraStreamAmount: number;
  code: string;
  iconCode: string;
  packageColor: string;
  secondaryPackageColor: string;
  packageExcerpt: string;
  bundleExcerpt: string;
  termsAndConditionDisclaimer: string;
  streamSavingsPrice: number;
  includesStream: boolean;
  isSeasonPass: boolean;
  featuredChannels: FeaturedChannel[];
  productImage: string;
  contractTerm?: number;
  contract?: {
    bundleDefinitionId: number;
    bundlePrice: number;
    bundleDiscount: number;
    bundleName: string;
  };
};

export type FeaturedChannel = {
  channelName: string;
  channelLogo: string;
  genre: string;
  packageCode?: string;
};

export class PackageListService {
  private static apiUmbracoBuilder = new EndpointHelper(`${environment.UTILITY_API_URI}/uni-api/dvs`);

  static getPrimaryPackages(customerNumber: string, country: string, agreementId = ''): Promise<any> {
    const url = this.apiUmbracoBuilder
      .createUrl('getEligiblePackages')
      .addBaseRouteParam(`${customerNumber}`)
      .addRouteParam(`${country}`)
      .addQueryParam('Channel', environment.MAH_API_CHANNEL)
      .build();

    const storedItems = StorageHelper.GetInstance.getFromCache('cnt', `eligiblePackages`, CacheAreaKey.Content);
    if (storedItems) {
      return Promise.resolve(storedItems as any);
    }

    return HttpClient.GetInstance.post<any, unknown>(url, { agreementId }, { retry: 2 }).then(
      (res: AxiosResponse<any>) =>
        StorageHelper.GetInstance.setCacheAndReturn('cnt', CacheAreaKey.Content, res.data, `eligiblePackages`) as any,
      (err) => {
        throw new Error(err);
      }
    );
  }

  static getContractPackages(country: string) {
    const url = this.apiUmbracoBuilder
      .createUrl('Bundle/Recommended/SplOtt')
      .addQueryParam('Country', country)
      .addQueryParam('BusinessUnit', environment.MAH_API_BUSINESS_UNIT)
      .addQueryParam('Channel', environment.MAH_API_CHANNEL)
      .build();

    const storedItems = StorageHelper.GetInstance.getFromCache('cnt', `contractPackages`, CacheAreaKey.Content);
    if (storedItems) {
      return Promise.resolve(storedItems as any);
    }

    return HttpClient.GetInstance.get<any>(url, { retry: 2 }).then(
      (res: AxiosResponse<any>) => {
        StorageHelper.GetInstance.setCacheAndReturn('cnt', CacheAreaKey.Content, res.data, `contractPackages`);
        return res.data;
      },
      (err) => {
        throw new Error(err);
      }
    );
  }

  static getContractPackage(country: string, principalPackage: string, contractPeriod: number) {
    const url = this.apiUmbracoBuilder
      .createUrl('Bundle/Recommended/SplOtt')
      .addQueryParam('Country', country)
      .addQueryParam('BusinessUnit', environment.MAH_API_BUSINESS_UNIT)
      .addQueryParam('Channel', environment.MAH_API_CHANNEL)
      .build();

    return HttpClient.GetInstance.post<any, unknown>(url, { principalPackage, contractPeriod }, { retry: 2 }).then(
      (res: AxiosResponse<any>) => {
        return res.data;
      },
      (err) => {
        throw new Error(err);
      }
    );
  }

  static getSeasonPassPackages(customerNumber: string, country: string, agreementId = ''): Promise<any> {
    const url = this.apiUmbracoBuilder
      .createUrl('getAvailableSeasonPass')
      .addBaseRouteParam(`${customerNumber}`)
      .addRouteParam(`${country}`)
      .addQueryParam('Channel', environment.MAH_API_CHANNEL)
      .build();

    const storedItems = StorageHelper.GetInstance.getFromCache('cnt', `availableSeasonPass`, CacheAreaKey.Content);
    if (storedItems) {
      return Promise.resolve(storedItems as any);
    }

    return HttpClient.GetInstance.get<any>(url, { retry: 2 }).then(
      (res: AxiosResponse<any>) =>
        StorageHelper.GetInstance.setCacheAndReturn(
          'cnt',
          CacheAreaKey.Content,
          res.data,
          `availableSeasonPass`
        ) as any,
      (err) => {
        throw new Error(err);
      }
    );
  }

  static getSubscriptions(customerNumber: string, country: string): Promise<any> {
    const url = this.apiUmbracoBuilder
      .createUrl('viewSubscription')
      .addBaseRouteParam(`${customerNumber}`)
      .addRouteParam(`${country}`)
      .addQueryParam('Channel', environment.MAH_API_CHANNEL)
      .build();

    return HttpClient.GetInstance.get<any>(url, { retry: 2 }).then(
      (res: AxiosResponse) => {
        return res.data;
      },
      (err) => {
        return { subscriptions: [] };
        // throw new Error(err);
      }
    );
  }

  static getFlexSubscriptions(customerNumber: string, country: string): Promise<any> {
    const url = this.apiUmbracoBuilder
      .createUrl('selectviewsubscription')
      .addBaseRouteParam(`${customerNumber}`)
      .addRouteParam(`${country}`)
      .addQueryParam('Channel', environment.MAH_API_CHANNEL)
      .build();

    return HttpClient.GetInstance.get<any>(url, { retry: 2 }).then(
      (res: AxiosResponse) => {
        return res.data;
      },
      (err) => {
        return { subscriptions: [] };
      }
    );
  }

  static getSubscriptionsWithContent(customerNumber: string, country: string): Promise<any> {
    return Promise.all([
      PackageListService.getSubscriptions(customerNumber, country),
      environment.DISABLE_SELECT
        ? Promise.resolve({ subscriptions: [] })
        : PackageListService.getFlexSubscriptions(customerNumber, country),
      ContentService.getPackages(),
    ]).then(([subscriptionResponse, flexSubscriptions, content]) => {
      return {
        content,
        flexSubscriptions: flexSubscriptions.subscriptions,
        subscriptions: subscriptionResponse.subscriptions,
      };
    });
  }

  static getPackagesWithContent(customerNumber: string, country: string): Promise<any> {
    return Promise.all([
      PackageListService.getPrimaryPackages(customerNumber, country).then((packages) =>
        packages.principalPackages.map((item: any) => ({ ...item, isSeasonPass: false }))
      ),
      PackageListService.getContractPackages(country).then((packages) => {
        return {
          packages: packages.principalPackages.map((item: any) => ({ ...item, isSeasonPass: false })),
          contractTerm: packages?.contractPeriods[0].id,
        };
      }),
      environment.USE_SEASON_PASS_API
        ? PackageListService.getSeasonPassPackages(customerNumber, country).then(
            (passes) => passes?.seasonPass?.map((item: any) => ({ ...item, isSeasonPass: true })),
            (error) => {
              console.error('Error Fetching passes -> ', error);
              return [];
            }
          )
        : Promise.resolve([]),
      ContentService.getPackages(),
    ]).then(([primaryPackageResponse, contractPackageResponse, passesResponse, content]) => {
      return [...primaryPackageResponse, ...passesResponse].map((subscription: any) => {
        const matchingContent = content.find(
          (item) => `${item.Code}`.toLowerCase() === `${subscription.serviceName}`.toLowerCase()
        );

        // Check if this principle package exists in the contract packages list
        const isInContractPackages = contractPackageResponse.packages.some(
          (contractPackage: any) => contractPackage.name.toLowerCase() === subscription.serviceName.toLowerCase()
        );

        return {
          serviceName: subscription.serviceName,
          description: subscription.description,
          price: subscription.price?.value,
          isSeasonPass: subscription.isSeasonPass,
          extraStreamAmount: matchingContent?.ExtraStreamPrice,
          code: matchingContent?.Code,
          iconCode: matchingContent?.IconCode,
          packageColor: matchingContent?.PackageColor,
          secondaryPackageColor: matchingContent?.SecondaryPackageColor,
          streamSavingsPrice: matchingContent?.StreamSavingsPrice,
          includesStream: matchingContent?.IncludesStream,
          packageExcerpt: matchingContent?.PackageExcerpt,
          bundleExcerpt: matchingContent?.BundleExcerpt,
          termsAndConditionDisclaimer: matchingContent?.TermsAndConditionDisclaimer,
          ...(isInContractPackages && { contractTerm: contractPackageResponse.contractTerm }),
        } as PrincipalPackage;
      });
    });
  }

  static getFlexPackagesContent(selectedPackages: string[]): Promise<any> {
    return Promise.all([
      ContentService.getPackages(),
      ContentService.getChannels(),
      ContentService.getChannelGenres(),
    ]).then(([content, channels, channelGenres]) => {
      return selectedPackages.map((packageCode: string) => {
        const matchingContent = content.find((item) => `${item.Code}`.toLowerCase() === packageCode.toLowerCase());
        return {
          description: matchingContent?.ProductName,
          price: matchingContent?.Price,
          extraStreamAmount: matchingContent?.ExtraStreamPrice,
          code: matchingContent?.Code,
          iconCode: matchingContent?.IconCode,
          packageColor: matchingContent?.PackageColor,
          secondaryPackageColor: matchingContent?.SecondaryPackageColor,
          streamSavingsPrice: matchingContent?.StreamSavingsPrice,
          includesStream: matchingContent?.IncludesStream,
          packageExcerpt: matchingContent?.PackageExcerpt,
          bundleExcerpt: matchingContent?.BundleExcerpt,
          termsAndConditionDisclaimer: matchingContent?.TermsAndConditionDisclaimer,
          featuredChannels: this.mapChannelsToFeaturedChannels(
            channels,
            channelGenres,
            matchingContent?.FeaturedChannels
          ),
          productImage: matchingContent?.ProductImage,
        } as PrincipalPackage;
      });
    });
  }

  static mapChannelsToFeaturedChannels(
    channels: ChannelContent[],
    channelGenres: ChannelGenreContent[],
    featuredChannels?: number[]
  ): FeaturedChannel[] | undefined {
    return featuredChannels?.map((channelId: number) => {
      const matchingContent = channels.find((item: any) => item.Id === `${channelId}`);
      return {
        channelLogo: matchingContent?.ChannelLogo,
        channelName: matchingContent?.ChannelName,
        genre: this.mapGenresToFeaturedChannels(channelGenres, matchingContent?.ChannelGenre),
      } as FeaturedChannel;
    });
  }

  static mapGenresToFeaturedChannels(channelGenre: ChannelGenreContent[], genres?: number[]): string | undefined {
    if (!genres?.length) return undefined;
    return genres?.reduce((genre: string, genreId: number) => {
      const matchingContent = channelGenre.find((itm) => itm.Id === `${genreId}`);
      return genre.length > 0 ? genre + `/ ${matchingContent?.GenreName}` : genre + matchingContent?.GenreName;
    }, '');
  }

  static cancelPackage(
    customerNumber: string,
    country: string,
    subscriptionBody: any,
    switchToStream: boolean
  ): Promise<any> {
    const url = this.apiUmbracoBuilder
      .createUrl('cancelPackage')
      .addBaseRouteParam(`${customerNumber}`)
      .addRouteParam(`${country}`)
      .addQueryParam('SwitchToStream', switchToStream)
      .addQueryParam('Channel', environment.MAH_API_CHANNEL)
      .build();

    return HttpClient.GetInstance.post<any, unknown>(url, subscriptionBody).then(
      (res: AxiosResponse) => {
        return res.data;
      },
      (err) => {
        throw new Error(err);
      }
    );
  }
}
